import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrgToken } from '@rsos/sinatra';
import { getProductToken } from '../utils/getProductToken';

// TODO_FALCON Extract to a time module
const MINUTE = 60 * 1000;
/**
 * A pre-emptive threshold that will trigger a refresh before the token's
 * actual expiration time.
 * e.g: If the threshold is set to 1 minute, a token set to expire at 12pm,
 * will refresh one minute earlier at 11:59pm.
 */
const EXPIRATION_THRESHOLD = 5 * MINUTE;

const useRenewProductToken = (
  org,
  productName,
  isRenewingRef,
  expirationThreshold = EXPIRATION_THRESHOLD,
) => {
  const dispatch = useDispatch();

  const productToken = useSelector(state => {
    const { productToken = '' } = getProductToken(state.sinatra);
    return productToken;
  });

  const productTokenExpiration = useSelector(state => {
    const { productTokenExpiration = '' } = getProductToken(state.sinatra);
    return productTokenExpiration;
  });

  const [nextTimerTick, setNextTimerTick] = useState(false);

  const renew = useCallback(async () => {
    if (isRenewingRef.current || !org) {
      return;
    }
    try {
      isRenewingRef.current = true;
      await dispatch(fetchOrgToken(org.id, productName));
      isRenewingRef.current = false;
    } finally {
      isRenewingRef.current = false;
    }
  }, [dispatch, isRenewingRef, org, productName]);

  useEffect(() => {
    if (!org || !productToken || isRenewingRef.current) {
      return;
    }
    const currentTime = new Date().getTime();
    const exactExpirationTime = productTokenExpiration - expirationThreshold;
    if (exactExpirationTime <= currentTime) {
      (async () => {
        await renew();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRenewingRef.current) {
      return;
    }
    (async () => {
      await renew();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextTimerTick]);

  // If there isn't a productToken already, and you have an org to fetch one
  // for, then do so
  useEffect(() => {
    if (!productToken) {
      renew();
    }
  }, [productToken, org?.id, renew]);

  useEffect(() => {
    const currentTime = new Date().getTime();
    const exactExpirationTime = productTokenExpiration - expirationThreshold;
    const nextTick = exactExpirationTime - currentTime;

    setTimeout(() => {
      setNextTimerTick(prev => !prev);
    }, nextTick);
  }, [productTokenExpiration, expirationThreshold]);

  return { token: productToken };
};

export default useRenewProductToken;
